import { bunja } from 'bunja';
import { atom } from 'jotai';

import { V2SocketEvents } from '@flipster/r230522/original/socket';
import AbstractSocket from '@flipster/socket/AbstractSocket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { tradeLogger } from '@flipster/utils/datadog';
import segment from '@flipster/utils/segment';
import { PerformanceEvent } from '@flipster/utils/segment/events/performance';

import { BridgeN2FR230522Socket } from '../normal-sub';

export const bridgeR230522Socket = new BridgeN2FR230522Socket();

type StopFn = () => void;

export const bridgeSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const bridgeFixedSubSocket = bridgeR230522Socket as AbstractSocket<unknown, V2SocketEvents>;
  const hasBridgeConnectionErrorAtom = atom(false);

  bunja.effect(() =>
    startBridgeConnectionErrorDetection((error) => {
      store.set(hasBridgeConnectionErrorAtom, true);
      tradeLogger.error(
        'webview_bridge_not_connected',
        { error_category: 'network_errors', error_message: error.message },
        error,
      );
    }),
  );

  return { bridgeFixedSubSocket, hasBridgeConnectionErrorAtom };
});
bridgeSocketBunja.debugLabel = 'bridgeSocketBunja';

function startBridgeConnectionErrorDetection(
  onErrorDetected: (error: Error) => void,
  isAllowedError: (error: Error) => boolean = () => false,
): StopFn {
  let connectionFailureCount = 0;
  const offConnected = bridgeR230522Socket.on('connected', () => void (connectionFailureCount = 0));
  const offConnectionError = bridgeR230522Socket.on('connectionError', (error: Error) => {
    if (isAllowedError(error)) return;
    connectionFailureCount++;
    if (connectionFailureCount !== 10) return;
    onErrorDetected(error);
  });
  return () => {
    offConnected();
    offConnectionError();
  };
}

export function startBridgeConnectionDurationLogging(): StopFn {
  let connectionStartTime: number | null = null;
  const offStateChanged = bridgeR230522Socket.on('stateChanged', (state) => {
    switch (state) {
      case 'connecting': {
        connectionStartTime = performance.now();
        break;
      }
      case 'closed': {
        connectionStartTime = null;
        break;
      }
      case 'connected': {
        if (!connectionStartTime) break;
        const now = performance.now();
        const durationNanoseconds = (now - connectionStartTime) * 1_000_000;
        const params = {
          cpe_name: 'webview_bridge_connected',
          domain: 'trade',
          log_version: 1,
          duration: durationNanoseconds,
        } satisfies PerformanceEvent['perf_webview_bridge_connected'];
        tradeLogger.info('webview_bridge_connected', params);
        segment.track('perf_webview_bridge_connected', params);
        break;
      }
    }
  });
  return () => {
    offStateChanged();
  };
}
